import { render } from './render.js';
import { collectCallbacks } from './withHandlers.js';

/**
 * Resolve a prebuilt key→fn map or walk an ElementConfig tree.
 * Config trees are arrays or objects with `tag`; everything else is treated as a map.
 * @param {object|Record<string, Function>} mapOrConfig
 * @returns {Record<string, Function>}
 */
function resolveMap(mapOrConfig) {
  if (mapOrConfig == null) return {};
  if (Array.isArray(mapOrConfig) || (typeof mapOrConfig === 'object' && mapOrConfig.tag != null)) {
    return collectCallbacks(mapOrConfig);
  }
  return mapOrConfig;
}

/**
 * Attach one delegated click listener for [data-action] (or custom selector).
 * Framework-agnostic: native EventTarget only.
 *
 * @param {EventTarget|null|undefined} root
 * @param {object|Record<string, Function>} mapOrConfig
 * @param {{ selector?: string, attr?: string, ctx?: object }} [options]
 * @returns {() => void} unmount (idempotent)
 */
export function bindActions(root, mapOrConfig, options = {}) {
  if (!root || typeof root.addEventListener !== 'function') {
    return function unmount() {};
  }

  const selector = options.selector != null ? options.selector : '[data-action]';
  const attr = options.attr != null ? options.attr : 'data-action';
  const ctx = options.ctx && typeof options.ctx === 'object' ? options.ctx : {};
  const map = resolveMap(mapOrConfig);

  const onClick = (event) => {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    const element = target.closest(selector);
    if (!element) return;

    // Stay within the bound root when root is a Node
    if (typeof root.contains === 'function' && !root.contains(element)) return;

    const key = element.getAttribute(attr);
    if (key == null || key === '') return;

    const fn = map[key];
    if (typeof fn !== 'function') return;

    fn({ key, event, element, ...ctx });
  };

  root.addEventListener('click', onClick);

  let removed = false;
  return function unmount() {
    if (removed) return;
    removed = true;
    root.removeEventListener('click', onClick);
  };
}

/**
 * Render config into root and bind data-action click handlers.
 *
 * @param {HTMLElement|null|undefined} root
 * @param {object|Array} config
 * @param {{ selector?: string, attr?: string, ctx?: object }} [options]
 * @returns {{ html: string, map: Record<string, Function>, unmount: () => void }}
 */
export function mount(root, config, options = {}) {
  const ctx = options.ctx && typeof options.ctx === 'object' ? options.ctx : {};
  const html = render(config, ctx);
  const map = collectCallbacks(config);

  if (root && typeof root === 'object' && 'innerHTML' in root) {
    root.innerHTML = html;
  }

  const unmount = bindActions(root, map, options);
  return { html, map, unmount };
}
